import { useState } from 'react';
import PageIntroBand from 'components/PageIntroBand';
import copy from 'data/tmsPage.json';
import { usePublishedHero } from 'hooks/usePublishedHero';
import { FlowButton } from 'components/ui/flow-button';
import HeroBackground from 'components/HeroBackground';
import './TMSPage.css';

import { useReveal } from 'hooks/useReveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import {
  HERO_CTA_PRIMARY_CLASS,
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_BODY_TEXT_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
} from 'components/SiteHeroCtas';
// Temporary web photography for layout review; replace with approved assets before publication.
const TMS_EDITORIAL_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1565891741441-64926e441838?auto=format&fit=crop&w=1500&q=85', alt: 'Trucks and trailers at warehouse loading docks' },
  { src: 'https://intelitaly.it/uploads/media/x1000/01/11-Intel-italy-spedizioni-italia-europa.jpg?v=1-0', alt: 'A truck travelling on a highway through open countryside' },
  { src: 'https://mecaluxuk.cdnwm.com/img/blog/dispatch-last-process.1.5.jpg?imdensity=1&imwidth=1024', alt: 'Warehouse dispatch activity beside loading doors' },
];

const TMS_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';


export default function TMSPage() {
  const heroMedia = usePublishedHero('tmsHero');
  const ref = useReveal();
  const [openDepartments, setOpenDepartments] = useState(['department-0']);

  return (
    <div ref={ref} className="min-h-screen bg-canvas tms-page">
      {/* HERO */}
      <section data-theme-surface="dark" data-testid="tms-hero" className={`${TMS_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={heroMedia.imageUrl} imageAlt={heroMedia.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <div className="absolute top-24 right-12 hidden lg:block"><div className="w-16 h-16 border-t border-r border-gold/10" /></div>

        <div className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="max-w-3xl">
            <p className={PAGE_HERO_EYEBROW_CLASS}>{copy.hero.eyebrow}</p>
            <h1 className={PAGE_HERO_H1_CLASS}>
              Trucking TMS for <span className="shimmer-text italic">the whole company.</span>
            </h1>
            <p className="mt-4 font-display text-xl text-accent-ink/90 italic tracking-tight sm:text-2xl">
              {copy.hero.subtitle}
            </p>
            {copy.hero.paragraphs.map(paragraph => (
              <p key={paragraph} className={PAGE_HERO_BODY_TEXT_CLASS}>{paragraph}</p>
            ))}
            <div className="space-y-5">
              <div className={PAGE_HERO_CTA_ROW_CLASS}>
                <FlowButton to="/#contact" data-testid="tms-hero-cta-demo" className={`${HERO_CTA_PRIMARY_CLASS} `}>
                  {copy.links.demo.label}
                </FlowButton>
                <FlowButton as="a"
                  href="https://tms.magellan.international/?login=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tms-hero-cta-signin"
                  className={HERO_CTA_SECONDARY_CLASS}
                >
                  {copy.links.signin.label}
                </FlowButton>
              </div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-copy/30 font-body text-pretty">
                {copy.hero.footnote}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div data-testid="tms-moving-strip" className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...copy.topics, ...copy.topics].map((item, index) => (
              <span key={`${item}-${index}`} aria-hidden={index >= copy.topics.length ? true : undefined} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}<span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <PageIntroBand testId="tms-page-intro" {...copy.intro} />

      {/* STAT STRIP */}
      <section data-testid="tms-stats" className="border-y border-ink/10 bg-surface">
        <div className="site-content-shell grid grid-cols-2 divide-ink/[0.07] md:grid-cols-4 md:divide-x">
          {copy.stats.map((stat) => (
            <div key={stat.label} className="flex flex-col items-start gap-1 px-2 py-10 md:items-center md:px-6 md:text-center">
              <span className="font-display text-4xl text-accent-ink md:text-5xl">{stat.value}</span>
              <span className="font-body text-[11px] uppercase tracking-[0.18em] text-copy/40">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="tms-different" className="tms-editorial-section">
        <div className="tms-editorial-shell">
          <header className="tms-editorial-heading">
            <p className="tms-kicker">{copy.stories.label}</p>
            <h2>{copy.stories.title}</h2>
            <p>{copy.stories.summary}</p>
          </header>
          {[0, 1, 2].map(group => <div className={`tms-story-spread tms-story-spread--${group}`} key={group}>
            <figure className="tms-story-image">
              <img src={TMS_EDITORIAL_IMAGES[group].src} alt={TMS_EDITORIAL_IMAGES[group].alt} loading="lazy" />
              <figcaption><span>CarrierFlow / 0{group+1}</span><span>{['From delivery to billing.','Drivers and office teams.','Documents and daily decisions.'][group]}</span></figcaption>
            </figure>
            <div className="tms-story-copy">
              {copy.stories.entries.slice(group*2,group*2+2).map((diff,index)=><article key={diff.title} data-testid={`tms-diff-${group*2+index}`}>
                <span className="tms-kicker">0{group*2+index+1}</span>
                <h3>{diff.title}</h3><p>{diff.description}</p><p className="tms-editorial-aside">{diff.aside}</p>
              </article>)}
            </div>
          </div>)}
        </div>
      </section>

      <section data-testid="tms-loop" className="tms-editorial-section tms-editorial-tint">
        <div className="tms-editorial-shell tms-journey-layout">
          <div className="tms-journey-opening"><p className="tms-kicker">{copy.journey.label}</p><h2>{copy.journey.title}</h2><img src={TMS_EDITORIAL_IMAGES[0].src} alt="Trucks and trailers at warehouse loading docks" loading="lazy" /></div>
          <ol className="tms-journey-list">{copy.journey.entries.map((entry,i)=><li key={entry.title}><span className="tms-step-number">0{i+1}</span><div><h3>{entry.title}</h3><p>{entry.description}</p></div></li>)}</ol>
        </div>
      </section>

      <section data-testid="tms-cockpits" className="tms-editorial-section">
        <div className="tms-editorial-shell">
          <header className="tms-departments-heading"><div><p className="tms-kicker">{copy.departments.label}</p><h2>{copy.departments.title}</h2></div><p>{copy.departments.summary}</p></header>
          <div className="tms-department-index">{copy.departments.groups.map((group, i) => <article key={group.title} data-testid={`tms-capability-${i}`}><span className="tms-step-number">0{i+1}</span><div><h3>{group.title}</h3><p>{group.summary}</p></div></article>)}</div>
          <Accordion type="multiple" value={openDepartments} onValueChange={setOpenDepartments} data-testid="tms-feature-catalogue" className="tms-feature-catalogue w-full">
            {copy.departments.groups.map((group, i) => {
              const value = `department-${i}`;
              const expanded = openDepartments.includes(value);
              return (
                <AccordionItem key={group.title} value={value} className="border-ink/10">
                  <AccordionTrigger className="tms-feature-trigger text-left font-display text-base text-ink hover:text-accent-ink hover:no-underline md:text-lg">{group.title}</AccordionTrigger>
                  <AccordionContent forceMount hidden={!expanded} aria-hidden={!expanded} data-tms-feature-panel="" className="tms-feature-content font-body text-sm leading-[1.9] text-copy/45">
                    <ul className="tms-feature-list">{group.features.map(feature => <li key={feature.label}><strong>{feature.label}:</strong> {feature.text}</li>)}</ul>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      <section data-testid="tms-connected-actions" className="tms-editorial-section tms-editorial-tint">
        <div className="tms-editorial-shell">
          <header className="tms-editorial-heading"><p className="tms-kicker">{copy.actions.label}</p><h2>{copy.actions.title}</h2></header>
          <ol className="tms-journey-list tms-connected-list">{copy.actions.entries.map((entry, i) => <li key={entry.title}><span className="tms-step-number">0{i+1}</span><div><h3>{entry.title}</h3><p>{entry.description}</p></div></li>)}</ol>
        </div>
      </section>

      <section data-testid="tms-company-controls" className="tms-editorial-section">
        <div className="tms-editorial-shell">
          <header className="tms-editorial-heading"><p className="tms-kicker">{copy.controls.label}</p><h2>{copy.controls.title}</h2></header>
          <ul className="tms-feature-list tms-control-list">{copy.controls.features.map(feature => <li key={feature.label}><strong>{feature.label}:</strong> {feature.text}</li>)}</ul>
        </div>
      </section>

      <section data-testid="tms-scopes" className="tms-editorial-section tms-editorial-tint">
        <div className="tms-editorial-shell">
          <header className="tms-editorial-heading"><p className="tms-kicker">{copy.scopes.label}</p><h2>{copy.scopes.title}</h2></header>
          <div className="tms-scope-list">{copy.scopes.entries.map((scope, i) => <article key={scope.name} data-testid={`tms-scope-${i}`} className="tms-scope-row"><div><span className="tms-kicker">0{i+1} / Operating scope</span><h3>{scope.name}</h3><p className="tms-editorial-aside">{scope.line}</p>{scope.featured && <span className="tms-scope-note">Freight and finance</span>}</div><p>{scope.description}</p><FlowButton to={copy.links.scope.path} data-testid={`tms-scope-cta-${i}`}>{scope.ctaLabel}</FlowButton></article>)}</div>
          <p className="tms-pricing-note">{copy.scopes.note}</p>
        </div>
      </section>

      <section data-testid="tms-implementation" className="tms-editorial-section">
        <div className="tms-editorial-shell tms-rollout-layout">
          <div className="tms-rollout-image"><img src={TMS_EDITORIAL_IMAGES[1].src} alt={TMS_EDITORIAL_IMAGES[1].alt} loading="lazy" /></div>
          <div><p className="tms-kicker">{copy.setup.label}</p><h2>{copy.setup.title}</h2><ol className="tms-rollout-list">{copy.setup.entries.map(step=><li key={step.number}><span className="tms-step-number">{step.number}</span><div><h3>{step.title}</h3><p>{step.description}</p></div></li>)}</ol></div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="tms-faq" className="border-t border-ink/10 bg-surface py-28 md:py-36">
        <div className="site-content-shell">
          <div className="flex items-center gap-3 mb-4 reveal"><div className="w-8 h-px bg-gold" /><p className="text-[11px] uppercase tracking-[0.3em] text-accent-ink/80 font-body">{copy.faq.label}</p></div>
          <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-4xl tracking-tight text-ink mb-12">{copy.faq.title}</h2>
          <Accordion type="single" collapsible className="w-full">
            {copy.faq.entries.map((faq, i) => (
              <AccordionItem key={faq.question} value={`faq-${i}`} className="border-ink/10">
                <AccordionTrigger className="text-left font-display text-base text-ink hover:text-accent-ink hover:no-underline md:text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-body text-sm leading-[1.9] text-copy/45">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section data-testid="tms-closing" className="border-t border-ink/10 bg-canvas px-6 py-24 text-center md:px-12 md:py-32">
        <div className="mx-auto max-w-4xl">
          <div className="mb-8 flex items-center justify-center gap-4">
            <span className="h-px w-14 bg-gold/35" />
            <span className="h-2 w-2 rotate-45 border border-gold/60" />
            <span className="h-px w-14 bg-gold/35" />
          </div>
          <h2 className="font-display text-4xl font-light leading-[0.98] tracking-tight text-ink md:text-6xl">
            {copy.closing.title}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl font-body text-sm leading-relaxed text-copy/40">
            {copy.closing.description}
          </p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <FlowButton to="/#contact" data-testid="tms-closing-cta" className={`${HERO_CTA_PRIMARY_CLASS} `}>
              {copy.links.demo.label}
            </FlowButton>
            <FlowButton to={copy.links.services.path} className={HERO_CTA_SECONDARY_CLASS}>
              {copy.links.services.label}
            </FlowButton>
          </div>
        </div>
      </section>
    </div>
  );
}
